import { useEffect, useState } from 'react'
import { X, Loader2, AlertCircle, Mail, Phone, Building2, Calendar, Clock, MapPin, BookOpen, CreditCard, History } from 'lucide-react'

import { Modal } from '@/ui/modal'
import { cn } from '@/core/lib/utils'

import { getAttendeeHistory } from '../api/attendeesService'
import type { AttendeeHistory } from '../api/attendeesService'

interface StudentHistoryModalProps {
  attendeeId: string | null
  open: boolean
  onClose: () => void
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {return '—'}
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {return String(value)}
  return date.toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function formatDate(value: string | null | undefined): string {
  if (!value) {return '—'}
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {return String(value)}
  return date.toLocaleDateString('en-CA', { dateStyle: 'medium' })
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {return '—'}
  return `$${Number(value).toFixed(2)} CAD`
}

export function StudentHistoryModal({ attendeeId, open, onClose }: StudentHistoryModalProps) {
  const [history, setHistory] = useState<AttendeeHistory | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !attendeeId) {
      setHistory(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const data = await getAttendeeHistory(attendeeId)
        setHistory(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load student history')
      } finally {
        setLoading(false)
      }
    })()
  }, [open, attendeeId])

  const attendee = history?.attendee
  const order = history?.order
  const payments = history?.payments ?? []
  const otherSessions = history?.otherSessions ?? []

  return (
    <Modal open={open} onClose={onClose} showCloseButton className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-navy">Student History</h2>
            <p className="text-sm text-slate-500">Complete enrollment and payment record</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading history...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {!loading && !error && attendee && (
          <>
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" />
                Student Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs">Name</span>
                  <span className="font-medium text-navy">
                    {`${attendee.firstName || ''} ${attendee.lastName || ''}`.trim() || '—'}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </span>
                  <span className="font-medium text-navy">{attendee.studentId || '—'}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </span>
                  <span className="font-medium text-navy">{attendee.phone || '—'}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> Company
                  </span>
                  <span className="font-medium text-navy">{attendee.company || '—'}</span>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Session Details
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Course</span>
                  <span className="font-medium text-navy">{attendee.sessionDetail?.courseName ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</span>
                  <span className="font-medium text-navy">{attendee.sessionDetail?.locationName ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</span>
                  <span className="font-medium text-navy">
                    {attendee.sessionDetail?.date ? formatDate(attendee.sessionDetail.date) : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Time</span>
                  <span className="font-medium text-navy">
                    {attendee.sessionDetail?.startTime ?? '—'}
                    {attendee.sessionDetail?.endTime ? ` – ${attendee.sessionDetail.endTime}` : ''}
                  </span>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <History className="w-4 h-4 text-primary" />
                Registration
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs">Status</span>
                  <span className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    attendee.status === 'paid' && 'bg-green-100 text-green-700',
                    attendee.status === 'registered' && 'bg-blue-100 text-blue-700',
                    attendee.status === 'pending' && 'bg-amber-100 text-amber-700',
                    attendee.status === 'completed' && 'bg-purple-100 text-purple-700',
                    attendee.status === 'quiz-completed' && 'bg-teal-100 text-teal-700'
                  )}>
                    {attendee.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs">Registered At</span>
                  <span className="font-medium text-navy">{formatDateTime(attendee.registeredAt)}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs">Checked In</span>
                  <span className="font-medium text-navy">{formatDateTime(attendee.checkedInAt)}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-slate-500 block text-xs">Quiz Score</span>
                  <span className={cn(
                    'font-medium',
                    attendee.quizScore !== null && attendee.quizScore >= 80 ? 'text-green-600' : 'text-navy'
                  )}>
                    {attendee.quizScore !== null ? `${attendee.quizScore}%` : '—'}
                  </span>
                </div>
              </div>
            </section>

            {order && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" />
                  Order & Payment
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Order ID</span>
                    <span className="font-medium text-navy font-mono">{String(order.id).slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className="font-medium text-navy capitalize">{String(order.status ?? '—')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total</span>
                    <span className="font-medium text-navy">{formatCurrency(order.total as number)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Paid At</span>
                    <span className="font-medium text-navy">{formatDateTime(order.paidAt as string)}</span>
                  </div>
                  {payments.length > 0 && (
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-slate-500 block text-xs mb-1">Payments</span>
                      {payments.map((payment, idx) => (
                        <div key={String(payment.id ?? idx)} className="flex justify-between text-xs">
                          <span className="text-slate-500">{String(payment.stripePaymentId ?? 'Payment')}</span>
                          <span className="font-medium text-navy">{formatCurrency(payment.amount as number)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {otherSessions.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <History className="w-4 h-4 text-primary" />
                  Other Enrollments
                </h3>
                <div className="space-y-2">
                  {otherSessions.map((session) => (
                    <div key={session.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-navy">{session.courseName ?? 'Course'}</span>
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          session.status === 'paid' && 'bg-green-100 text-green-700',
                          session.status === 'registered' && 'bg-blue-100 text-blue-700',
                          session.status === 'pending' && 'bg-amber-100 text-amber-700',
                          session.status === 'completed' && 'bg-purple-100 text-purple-700',
                          session.status === 'quiz-completed' && 'bg-teal-100 text-teal-700'
                        )}>
                          {session.status}
                        </span>
                      </div>
                      <div className="text-slate-500 text-xs mt-1">
                        {session.sessionDate && formatDate(session.sessionDate)}
                        {session.sessionLocation && ` • ${session.sessionLocation}`}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
